/** Mapa único de la navegación de administración: rutas, secciones y quién las ve. */
export interface AdminNavItem {
  label: string;
  icon: string;
  route: string;
  /** Rutas adicionales que mantienen resaltado el elemento (p. ej. editores hijos). */
  matches?: string[];
  /** Solo visible para ADMIN. */
  soloAdmin?: boolean;
}

export interface AdminNavSection {
  id: 'inicio' | 'operativa' | 'diseno' | 'configuracion';
  label: string;
  icon: string;
  route: string;
  soloAdmin?: boolean;
  items: AdminNavItem[];
}

export const ADMIN_NAV: AdminNavSection[] = [
  {
    id: 'inicio',
    label: 'Resumen',
    icon: 'pi pi-home',
    route: '/admin',
    soloAdmin: true,
    items: []
  },
  {
    id: 'operativa',
    label: 'Gestión operativa',
    icon: 'pi pi-chart-line',
    route: '/admin/operativa',
    items: [
      { label: 'Agenda y reservas', icon: 'pi pi-calendar', route: '/reservas', matches: ['/agenda'] },
      { label: 'Clientes', icon: 'pi pi-address-book', route: '/clientes' },
      { label: 'Servicios', icon: 'pi pi-briefcase', route: '/admin/servicios', soloAdmin: true },
      { label: 'Personal', icon: 'pi pi-id-card', route: '/admin/empleados', soloAdmin: true },
      { label: 'Usuarios y accesos', icon: 'pi pi-users', route: '/usuarios', soloAdmin: true }
    ]
  },
  {
    id: 'diseno',
    label: 'Diseño de página',
    icon: 'pi pi-palette',
    route: '/admin/diseno',
    soloAdmin: true,
    items: [
      { label: 'Portada', icon: 'pi pi-home', route: '/admin/home' },
      { label: 'Menús y enlaces', icon: 'pi pi-sitemap', route: '/admin/links' },
      { label: 'Páginas', icon: 'pi pi-file-edit', route: '/admin/paginas' },
      { label: 'Colores y estilo', icon: 'pi pi-sliders-h', route: '/admin/diseno/apariencia' }
    ]
  },
  {
    id: 'configuracion',
    label: 'Configuración general',
    icon: 'pi pi-cog',
    route: '/admin/configuracion',
    soloAdmin: true,
    items: []
  }
];

/** Rutas que forman el área de administración (todas protegidas por sus guards). */
const PREFIJOS_ADMIN = ['/admin', '/usuarios', '/clientes', '/reservas', '/agenda'];

export function esRutaAdmin(url: string): boolean {
  const ruta = rutaSinQuery(url);
  return PREFIJOS_ADMIN.some((p) => ruta === p || ruta.startsWith(p + '/'));
}

export function rutaSinQuery(url: string): string {
  return (url.split('?')[0].split('#')[0] || '/').replace(/\/+$/, '') || '/';
}

function coincide(ruta: string, destino: string): boolean {
  return ruta === destino || ruta.startsWith(destino + '/');
}

/** Elemento más específico que corresponde a la URL actual, para resaltar solo uno. */
export function elementoActivo(url: string): string | null {
  const ruta = rutaSinQuery(url);
  let mejor: { route: string; largo: number } | null = null;
  for (const s of ADMIN_NAV) {
    const candidatos = [s.route, ...s.items.flatMap((i) => [i.route, ...(i.matches ?? [])])];
    for (const c of candidatos) {
      const exacto = s.id === 'inicio' ? ruta === c : coincide(ruta, c);
      if (exacto && (!mejor || c.length > mejor.largo)) {
        const dueño = s.items.find((i) => i.route === c || i.matches?.includes(c));
        mejor = { route: dueño ? dueño.route : s.route, largo: c.length };
      }
    }
  }
  return mejor?.route ?? null;
}

export function seccionActiva(url: string): AdminNavSection | null {
  const activo = elementoActivo(url);
  if (!activo) return null;
  return ADMIN_NAV.find((s) => s.route === activo || s.items.some((i) => i.route === activo)) ?? null;
}
